import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchActiveSessions,
  fetchExpiredSessions,
  selectSessions,
  ActiveSession,
} from "./sessionsSlice";
import { forceLogoutUser } from "../setups/setupSlices";
import { selectPreference } from "../preferences/preferencesSlice";

import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import Paper from "@mui/material/Paper";
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Logout } from "@mui/icons-material";

import { StyledTableCell, StyledTableRow } from "../../styles/tableStyles";
import TableSkeleton from "../../components/Layout/TableSkeleton";
import Paging from "../../components/Layout/Paging";
import { useTranslation } from "react-i18next";

/** Compact "x min ago" label for a recent timestamp. */
const timeAgo = (value?: string): string => {
  if (!value) return "";
  const diffMs = Date.now() - new Date(value).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.floor(hrs / 24)} d ago`;
};

const formatDateTime = (value?: string): string =>
  value ? new Date(value).toLocaleString() : "";

const formatRevokeReason = (reason?: string): string => {
  if (reason === "idle") return "Idle timeout";
  if (reason === "manual") return "Manual revoke";
  return reason ?? "";
};

export const ActiveSessions = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const {
    sessionsWithCount: { sessions, totalCount },
    loading,
    expiredSessionsWithCount: { sessions: expiredSessions, totalCount: expiredTotalCount },
    expiredLoading,
  } = useAppSelector(selectSessions);
  const { searchText } = useAppSelector(selectPreference);

  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [expiredRowsPerPage, setExpiredRowsPerPage] = useState(20);
  const [expiredCurrentPage, setExpiredCurrentPage] = useState(0);

  const loadSessions = () => {
    dispatch(
      fetchActiveSessions({
        searchText,
        skip: currentPage * rowsPerPage,
        take: rowsPerPage,
      }),
    );
  };

  const loadExpiredSessions = () => {
    dispatch(
      fetchExpiredSessions({
        searchText,
        skip: expiredCurrentPage * expiredRowsPerPage,
        take: expiredRowsPerPage,
      }),
    );
  };

  useEffect(() => {
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, searchText, currentPage, rowsPerPage]);

  useEffect(() => {
    loadExpiredSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, searchText, expiredCurrentPage, expiredRowsPerPage]);

  const RevokeSession = async (row: ActiveSession) => {
    if (
      window.confirm(
        `Sign ${row.userName ?? "this user"} out of all devices now?`,
      )
    ) {
      await dispatch(forceLogoutUser(row.userId));
      loadSessions();
      loadExpiredSessions();
    }
  };

  const total = (totalCount as number) ?? 0;
  const expiredTotal = (expiredTotalCount as number) ?? 0;

  return (
    <>
      <Helmet>
        <title>{`Active Sessions | ${t("platform")}`}</title>
      </Helmet>
      <Box sx={{ m: { xs: 0, md: 1 }, px: { xs: 0, md: 2 } }}>
        <Typography variant="h6" color="textPrimary" sx={{ mb: 1 }}>
          Active Sessions
        </Typography>
        <Grid container justifyContent="flex-start" sx={{ mt: 1 }}>
          <TableContainer component={Paper}>
            <Table size="small" aria-label="active sessions table">
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell>S.No</StyledTableCell>
                  <StyledTableCell>Name</StyledTableCell>
                  <StyledTableCell>Email</StyledTableCell>
                  <StyledTableCell>Company</StyledTableCell>
                  <StyledTableCell>Logged in</StyledTableCell>
                  <StyledTableCell>Last active</StyledTableCell>
                  <StyledTableCell>Actions</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {loading === "pending" ? (
                  <TableSkeleton numRows={5} numColumns={7} />
                ) : (
                  sessions.map((row, index) => (
                    <StyledTableRow key={row._id}>
                      <StyledTableCell component="th" scope="row">
                        {currentPage * rowsPerPage + index + 1}
                      </StyledTableCell>
                      <StyledTableCell>{row.userName}</StyledTableCell>
                      <StyledTableCell>{row.userEmail}</StyledTableCell>
                      <StyledTableCell>{row.companyName}</StyledTableCell>
                      <StyledTableCell>
                        <Tooltip title={formatDateTime(row.loginAt)}>
                          <span>{formatDateTime(row.loginAt)}</span>
                        </Tooltip>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Tooltip title={formatDateTime(row.lastActivityAt)}>
                          <span>{timeAgo(row.lastActivityAt)}</span>
                        </Tooltip>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Tooltip title="Revoke (sign out of all devices)">
                          <IconButton
                            color="warning"
                            onClick={() => RevokeSession(row)}
                            size="large"
                          >
                            <Logout />
                          </IconButton>
                        </Tooltip>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Stack spacing={1} sx={{ mt: 1 }}>
          <Paging
            total={total}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
            setRowsPerPage={setRowsPerPage}
            setCurrentPage={setCurrentPage}
          />
          <Typography variant="h6" color="textPrimary" component="div">
            Active sessions: {total}
          </Typography>
        </Stack>

        <Typography variant="h6" color="textPrimary" sx={{ mt: 3, mb: 1 }}>
          Expired Sessions
        </Typography>
        <Grid container justifyContent="flex-start" sx={{ mt: 1 }}>
          <TableContainer component={Paper}>
            <Table size="small" aria-label="expired sessions table">
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell>S.No</StyledTableCell>
                  <StyledTableCell>Name</StyledTableCell>
                  <StyledTableCell>Email</StyledTableCell>
                  <StyledTableCell>Company</StyledTableCell>
                  <StyledTableCell>Logged in</StyledTableCell>
                  <StyledTableCell>Last active</StyledTableCell>
                  <StyledTableCell>Expired</StyledTableCell>
                  <StyledTableCell>Reason</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {expiredLoading === "pending" ? (
                  <TableSkeleton numRows={5} numColumns={8} />
                ) : (
                  expiredSessions.map((row, index) => (
                    <StyledTableRow key={row._id}>
                      <StyledTableCell component="th" scope="row">
                        {expiredCurrentPage * expiredRowsPerPage + index + 1}
                      </StyledTableCell>
                      <StyledTableCell>{row.userName}</StyledTableCell>
                      <StyledTableCell>{row.userEmail}</StyledTableCell>
                      <StyledTableCell>{row.companyName}</StyledTableCell>
                      <StyledTableCell>
                        <Tooltip title={formatDateTime(row.loginAt)}>
                          <span>{formatDateTime(row.loginAt)}</span>
                        </Tooltip>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Tooltip title={formatDateTime(row.lastActivityAt)}>
                          <span>{timeAgo(row.lastActivityAt)}</span>
                        </Tooltip>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Tooltip title={formatDateTime(row.revokedAt)}>
                          <span>{formatDateTime(row.revokedAt)}</span>
                        </Tooltip>
                      </StyledTableCell>
                      <StyledTableCell>
                        {formatRevokeReason(row.revokedReason)}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Stack spacing={1} sx={{ mt: 1 }}>
          <Paging
            total={expiredTotal}
            rowsPerPage={expiredRowsPerPage}
            currentPage={expiredCurrentPage}
            setRowsPerPage={setExpiredRowsPerPage}
            setCurrentPage={setExpiredCurrentPage}
          />
          <Typography variant="h6" color="textPrimary" component="div">
            Expired sessions: {expiredTotal}
          </Typography>
        </Stack>
      </Box>
    </>
  );
};

export default ActiveSessions;
